import Immerable from '@naruto/naruto-models/src/types/GeneralImmer';

import I18n from './I18n';
import defaultLang from './data/default';
import { LangAvailableList } from './types';

const I18N_LANG_KEY = 'naruto-i18n-lang';

class I18nManager extends Immerable {
  private _i18ns: { [key: string]: I18n } = {};

  constructor() {
    super();
    this._i18ns.default = new I18n(defaultLang, 0);
  }

  setLocaleData = (domain: string, config: LangAvailableList): void => {
    const mergedConfig = config.map(langConfig => {
      const defaultConfig = defaultLang.find(ele => ele.value === langConfig.value);
      const { name, value, translate } = langConfig;
      return {
        name,
        value,
        translate: {
          ...defaultConfig?.translate,
          ...translate
        }
      };
    });
    this._i18ns[domain] = new I18n(mergedConfig, 0);
  };

  setCurrentDomain = (domain: string): void => {
    if (!window.Naruto_I18n) {
      window.Naruto_I18n = { domain: 'default' };
    }

    const isAvailableDomain = this.availableDomains.includes(domain);
    if (isAvailableDomain) {
      window.Naruto_I18n.domain = domain;
    }
  };

  setCurrentLanguage = (languageValue: string): void => {
    const i18nInstance = this._getCurrentI18n();
    const isLangAvailable = i18nInstance.getLanguageIndex(languageValue) > -1;
    if (isLangAvailable) {
      localStorage.setItem(I18N_LANG_KEY, languageValue);
      i18nInstance.switchLanguage(languageValue);
    }
  };

  __ = (key: string): string => {
    const i18nInstance = this._getCurrentI18n();
    return i18nInstance.t(key);
  };

  _getCurrentI18n = (): I18n => {
    const currentDomain = this.currentDomain;
    const i18nInstance = this._i18ns[currentDomain];
    if (!i18nInstance) {
      throw new Error(`i18n instance of ${currentDomain} not found.`);
    }
    return i18nInstance;
  };

  resetI18ns = (): void => {
    delete window.Naruto_I18n;
    const i18nInstance = this._getCurrentI18n();
    i18nInstance.resetLanguage();
    localStorage.removeItem(I18N_LANG_KEY);
    this._clearAllDomain();
  };

  _clearAllDomain = (): void => {
    Object.keys(this._i18ns).forEach(domain => {
      if (domain !== 'default') {
        delete this._i18ns[domain];
      }
    });
  };

  get availableDomains(): string[] {
    return Object.keys(this._i18ns);
  }

  get currentDomain(): string {
    const currentDomain = window.Naruto_I18n?.domain || 'default';
    return currentDomain;
  }

  get currentLanguage(): string {
    const currentLanguage = localStorage.getItem(I18N_LANG_KEY) || '';
    return currentLanguage;
  }

  get currentI18nLanguage(): string {
    const currentI18nLanguage = this._getCurrentI18n().currentLanguageValue;
    return currentI18nLanguage;
  }

  get translationAdapter(): I18n {
    return this._getCurrentI18n();
  }
}

export default I18nManager;
